import {HttpErrorResponse} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {StorageKeys} from '@app/core/constants/storage-keys';
import {LocalStorageService} from '@app/core/services/local-storage.service';
import {ErrorHandlerUtils} from '@app/core/utils/error-handler.utils';
import {ObjectUtils} from '@app/core/utils/object.utils';
import {ToastMessageUtils} from '@app/core/utils/toast-message.utils';
import {ECustomerOrganizationStatus, ECustomerStatus, ECustomerUpdateStatus} from '@app/shared/enums/header.enum';
import {EToastMessageOptions, EToastMessageSeverity} from '@app/shared/enums/toast-message.enum';
import {ICustomerOrganization, ICustomerProfileState, IStatusState} from '@app/shared/models/header.model';
import {HeaderService} from '@app/shared/services/header.service';
import {environment} from '@environments/environment';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {MessageService} from 'primeng/api';
import {catchError, exhaustMap, map, of, switchMap, withLatestFrom} from 'rxjs';
import {setLanguageFromHeaderEffect} from '../language/language.action';
import {
  HeaderActions,
  getCustomerProfile,
  getCustomerProfileFailure,
  getCustomerProfileFromInstitutionSettingEffect,
  getCustomerProfileSuccess,
  loadCustomerOrganization,
  loadCustomerOrganizationSuccess,
  onChangeCustomerOrganization,
  saveCustomerOrganizationSuccess,
  setCustomerOrganizationStatusFromHeaderEffect,
  setUpdateCustomerProfileFailure,
  setUpdateCustomerProfileSuccess,
  updateCustomerProfile,
} from './header.action';
import {selectCustomerProfile} from './header.selector';

@Injectable()
export class HeaderEffects {
  private readonly actions$ = inject(Actions);
  private readonly headerService = inject(HeaderService);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly localStorageService = inject(LocalStorageService);

  globalToast = 'global-toast';

  getOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCustomerProfile, getCustomerProfileFromInstitutionSettingEffect),
      withLatestFrom(this.store.select(selectCustomerProfile)),
      exhaustMap(([action, cachedProfile]) =>
        action.useCache && cachedProfile ? of(cachedProfile) : this.headerService.getCustomerProfile()
      ),
      switchMap((customerProfileState: ICustomerProfileState) => {
        const customerPreferredLanguage = customerProfileState.languageId?.toString();
        if (customerPreferredLanguage) {
          this.translate.use(customerPreferredLanguage);
        }

        this.localStorageService.setItem(StorageKeys.currentLanguage, customerPreferredLanguage);
        this.store.dispatch(
          setLanguageFromHeaderEffect({
            languageId: customerPreferredLanguage ?? environment.defaultLanguage,
          })
        );
        this.localStorageService.setItem(StorageKeys.selectedUserRole, customerProfileState.roleId);
        return of(
          getCustomerProfileSuccess({
            customerProfileState: customerProfileState,
          })
        );
      }),
      catchError(() => of(getCustomerProfileFailure()))
    )
  );

  saveSelectedOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeaderActions.OnChangeCustomerOrganization),
      map((action: ReturnType<typeof onChangeCustomerOrganization>) => {
        this.localStorageService.setItem(StorageKeys.selectedOrganization, action.organization.organizationId);
        return saveCustomerOrganizationSuccess();
      })
    )
  );

  loadSelectedOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeaderActions.LoadCustomerOrganization),
      withLatestFrom(this.store.select(selectCustomerProfile)),
      map(([, customerProfileState]: [ReturnType<typeof loadCustomerOrganization>, ICustomerProfileState | null]) => {
        let filteredOrg: ICustomerOrganization = {} as ICustomerOrganization;
        if (customerProfileState) {
          const selectedOrganizationId = this.localStorageService.getItem(StorageKeys.selectedOrganization);
          filteredOrg =
            customerProfileState.customerOrganizations.find(x => x.organizationId == selectedOrganizationId) ??
            customerProfileState.customerOrganizations[0];
        }
        const accountStatus = this.getCustomerOrganizationStatus(
          filteredOrg,
          customerProfileState?.customerStatus ?? ({} as IStatusState)
        );
        this.store.dispatch(
          setCustomerOrganizationStatusFromHeaderEffect({
            accountStatus: accountStatus,
          })
        );
        this.store.dispatch(loadCustomerOrganizationSuccess({organization: filteredOrg}));
        return loadCustomerOrganizationSuccess({organization: filteredOrg});
      })
    )
  );

  UpdateCustomerProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeaderActions.UpdateCustomerProfile),
      switchMap((action: ReturnType<typeof updateCustomerProfile>) =>
        this.headerService.updateCustomerProfile(action.customerProfile).pipe(
          switchMap((customerProfileState: ICustomerProfileState) => {
            ToastMessageUtils.showMessage({
              messageService: this.messageService,
              toastKey: this.globalToast,
              severity: EToastMessageSeverity.Success,
              messageDetail: this.translate.instant('Profile.ProfileChangeSuccess'),
              closable: true,
              life: EToastMessageOptions.LifeTime,
            });
            return of(
              setUpdateCustomerProfileSuccess({
                customerProfileState: customerProfileState,
                updateCustomerStatus: ECustomerUpdateStatus.Success,
              })
            );
          }),
          catchError((error: HttpErrorResponse) => {
            const errMsg = ErrorHandlerUtils.getErrorMessage(this.translate, error.status);
            ToastMessageUtils.showMessage({
              messageService: this.messageService,
              toastKey: this.globalToast,
              severity: EToastMessageSeverity.Error,
              messageDetail: errMsg,
              closable: true,
              life: EToastMessageOptions.LifeTime,
            });
            return of(
              setUpdateCustomerProfileFailure({
                updateCustomerStatus: ECustomerUpdateStatus.Failure,
              })
            );
          })
        )
      )
    )
  );

  private getCustomerOrganizationStatus(
    filteredOrganization: ICustomerOrganization,
    customerStatus: IStatusState | null
  ): ECustomerOrganizationStatus {
    if (
      !ObjectUtils.isEmpty(filteredOrganization) &&
      filteredOrganization?.customerOrganizationStatus.id === ECustomerOrganizationStatus.Active &&
      filteredOrganization?.organization.organizationStatus?.id === ECustomerOrganizationStatus.Active &&
      customerStatus?.id === ECustomerStatus.Verified
    ) {
      return ECustomerOrganizationStatus.Active;
    }

    return ECustomerOrganizationStatus.New;
  }
}
